import { useRef } from 'react';
import {
  getAuth,
  RecaptchaVerifier,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
} from 'firebase/auth';

import { LOGIN_VIEWS } from '../policy/loginViews';
import { getSafeErrorIdentifier } from '../../utils/getSafeErrorIdentifier';

// Phone-number enrollment leg of the SMS machinery: render the invisible
// reCAPTCHA, send the verification SMS and enroll the factor with the first
// code the user types.
export default function useSmsEnrollment({
  language,
  tenantID,
  phoneRef,
  verificationID,
  setVerificationID,
  setCurrentView,
  setLoginErrorKey,
  onEnrolled,
  setOPT,
  setCaptchaCode,
  setIsverifySMSCode,
  setIsLoading,
}) {
  const sendGeneration = useRef(0);
  // The verifier this attempt anchored on #send-info-button: whoever ends the
  // attempt has to destroy it, or a second one binds to the same node.
  const enrollmentVerifierRef = useRef(null);

  function cancelEnrollmentSend() {
    sendGeneration.current += 1;
    // The node it is bound to is going away with the screen: a re-entry has to
    // build its own against the fresh one.
    try {
      enrollmentVerifierRef.current?.clear();
    } catch (error) {
      console.error(
        'useSmsEnrollment.cancelEnrollmentSend:',
        getSafeErrorIdentifier(error),
      );
    }
    enrollmentVerifierRef.current = null;
    setOPT(false);
  }

  function setUpRecaptha() {
    if (enrollmentVerifierRef.current) return enrollmentVerifierRef.current;
    setIsLoading(false);
    setLoginErrorKey(null);
    const auth = getAuth();
    auth.languageCode = language;
    // The signed-in user's own tenant, never the screen's: enrollment always
    // runs after a sign-in already landed (see verifyEnrollmentCode below),
    // and Google-first can land an internal account here from `/ext`.
    auth.tenantId = (auth.currentUser && auth.currentUser.tenantId) || tenantID;
    const generation = sendGeneration.current;
    const isStale = () => generation !== sendGeneration.current;
    const recaptchaVerifier = new RecaptchaVerifier(
      'send-info-button',
      {
        size: 'invisible',
        callback: function (response) {
          if (isStale()) return;
          let newPhone = phoneRef.current;
          const user = auth.currentUser;
          const PhoneNumberRemoveSpace = newPhone.replace(/\s+/g, '');
          try {
            multiFactor(user)
              .getSession()
              .then(function (multiFactorSession) {
                if (isStale()) return;
                try {
                  const phoneInfoOptions = {
                    phoneNumber: PhoneNumberRemoveSpace,
                    session: multiFactorSession,
                  };
                  const phoneAuthProvider = new PhoneAuthProvider(auth);
                  return phoneAuthProvider
                    .verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
                    .then(verificationId => {
                      if (isStale()) return;
                      try {
                        setVerificationID(verificationId);
                        setCurrentView(LOGIN_VIEWS.SMS_CODE);
                        // The send is over: leaving it set would reach the code
                        // screen with its inputs and buttons still disabled.
                        setOPT(false);
                      } catch (error) {
                        setOPT(false);
                        console.error(
                          'useSmsEnrollment.setUpRecaptha[setView]:',
                          getSafeErrorIdentifier(error),
                        );
                        setLoginErrorKey('heimdal_app.Login.try_again_later');
                      }
                    })
                    .catch(error => {
                      if (isStale()) return;
                      setOPT(false);
                      if (error.code === 'auth/too-many-requests') {
                        setLoginErrorKey('heimdal_app.Login.too_many_requests');
                      } else {
                        setLoginErrorKey('heimdal_app.Login.try_again_later');
                      }
                    });
                } catch (error) {
                  setOPT(false);
                  console.error(
                    'useSmsEnrollment.setUpRecaptha[verifyPhoneNumber]:',
                    getSafeErrorIdentifier(error),
                  );
                  setLoginErrorKey('heimdal_app.Login.try_again_later');
                }
              })
              .catch(error => {
                if (isStale()) return;
                setOPT(false);
                console.error(
                  'useSmsEnrollment.setUpRecaptha[getSession]:',
                  getSafeErrorIdentifier(error),
                );
                setLoginErrorKey('heimdal_app.Login.try_again_later');
              });
          } catch (error) {
            setOPT(false);
            console.error(
              'useSmsEnrollment.setUpRecaptha[getSession]:',
              getSafeErrorIdentifier(error),
            );
            setLoginErrorKey('heimdal_app.Login.try_again_later');
          }
        },
        'expired-callback': () => {
          if (isStale()) return;
          setOPT(false);
          console.error(
            'useSmsEnrollment.setUpRecaptha[expired-callback]: recaptcha timeout',
          );
        },
      },
      auth,
    );
    enrollmentVerifierRef.current = recaptchaVerifier;
    recaptchaVerifier.render();
    return recaptchaVerifier;
  }

  // Same contract as verifyChallengeCode: false means nothing reached Firebase,
  // so no later answer will free the parent's in-flight guard.
  function verifyEnrollmentCode(captchaCode) {
    const auth = getAuth();
    auth.languageCode = language;
    auth.tenantId = (auth.currentUser && auth.currentUser.tenantId) || tenantID;
    const user = auth.currentUser;
    try {
      const cred = PhoneAuthProvider.credential(verificationID, captchaCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
      multiFactor(user)
        .enroll(multiFactorAssertion, 'SMS')
        .then(_ => {
          try {
            const body = {
              idToken: user.stsTokenManager.accessToken,
              refreshToken: user.stsTokenManager.refreshToken,
            };
            onEnrolled(body);
          } catch (error) {
            setOPT(false);
            setLoginErrorKey('heimdal_app.Login.login_error');
            setIsverifySMSCode(false);
          }
        })
        .catch(error => {
          if (error.code === 'auth/invalid-verification-code') {
            setOPT(false);
            setCaptchaCode('');
            setLoginErrorKey('heimdal_app.Login.wrong_code');
          } else {
            setOPT(false);
            console.error(
              'useSmsEnrollment.verifyEnrollmentCode:',
              getSafeErrorIdentifier(error),
            );
            setLoginErrorKey('heimdal_app.Login.try_again_later');
          }
          setIsverifySMSCode(false);
        });
      return true;
    } catch (error) {
      setOPT(false);
      console.error(
        'useSmsEnrollment.verifyEnrollmentCode:',
        getSafeErrorIdentifier(error),
      );
      setLoginErrorKey('heimdal_app.Login.try_again_later');
      setIsverifySMSCode(false);
      return false;
    }
  }

  return { setUpRecaptha, verifyEnrollmentCode, cancelEnrollmentSend };
}
